import type {
  AssignRoutingConfigResponse,
  DeleteRoutingTimeWindowResponse,
  ListRoutingTimeWindowsResponse,
  PutRoutingTimeWindowRequest,
  PutRoutingTimeWindowResponse,
  RoutingTimeWindowRecord,
  CloneRoutingConfigRequest,
  CloneRoutingConfigResponse,
  CreateRoutingConfigFromDefaultRequest,
  CreateRoutingConfigFromDefaultResponse,
  DeleteRoutingSkuPolicyResponse,
  DeleteRoutingSkuTargetResponse,
  GetEffectiveTenantRouteResponse,
  GetOrgRoutingAssignmentsResponse,
  GetRoutingConfigSkuSummaryResponse,
  GetRoutingSkuPolicyResponse,
  GlobalDefaultParityResponse,
  ListRoutingConfigsResponse,
  ListRoutingSkuTargetsResponse,
  PutRoutingSkuPolicyRequest,
  PutRoutingSkuPolicyResponse,
  PutRoutingSkuTargetRequest,
  PutRoutingSkuTargetResponse,
  RenameRoutingConfigRequest,
  RenameRoutingConfigResponse,
  ApplyRoutingConfigSkuCoverageFillRequest,
  ApplyRoutingConfigSkuCoverageFillResponse,
  PreviewRoutingConfigSkuCoverageFillRequest,
  PreviewRoutingConfigSkuCoverageFillResponse,
  SetRoutingConfigCoverageModeRequest,
  SetRoutingConfigCoverageModeResponse,
  SetOrgRoutingAssignmentsRequest,
  SetOrgRoutingAssignmentsResponse,
  SetRoutingConfigDeprecationRequest,
  SetRoutingConfigDeprecationResponse,
  RoutingSkuPolicyRecord,
  RoutingSkuTargetRecord,
  SetGlobalRoutingConfigRequest,
  SetGlobalRoutingConfigResponse,
  UpstreamProtocol,
} from '@nadoutong/contracts';
import { OpsApiError, opsApiRequest } from './client.ts';

const BASE = '/v1/ops/tenant-routing';

export async function listRoutingConfigs(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListRoutingConfigsResponse> {
  return opsApiRequest<ListRoutingConfigsResponse>(
    `${BASE}/configs`,
    { signal },
    fetchImpl,
  );
}

export async function createRoutingConfigFromDefault(
  body: CreateRoutingConfigFromDefaultRequest,
  fetchImpl: typeof fetch,
): Promise<CreateRoutingConfigFromDefaultResponse> {
  return opsApiRequest<CreateRoutingConfigFromDefaultResponse>(
    `${BASE}/configs/from-default`,
    { method: 'POST', body },
    fetchImpl,
  );
}

/**
 * Switch the global routing config (#1421).
 *
 * `reason` is required by the gateway and is typed by the operator; it lands
 * verbatim in `routing_switch_events.reason`, which is the record the next
 * incident reads. The client refuses to send an empty one rather than
 * substituting a machine-written placeholder — a log full of "no reason given"
 * is worse than no log, because it looks like evidence.
 *
 * `preflight_token` is omitted for now: it is optional in this phase and the
 * console has no preflight report to take one from until the P3b panel lands.
 */
export async function setGlobalRoutingConfig(
  configId: string,
  body: SetGlobalRoutingConfigRequest,
  fetchImpl: typeof fetch,
): Promise<SetGlobalRoutingConfigResponse> {
  const reason = body.reason.trim();
  if (reason.length === 0) {
    throw new OpsApiError(
      400,
      'INVALID_PAYLOAD',
      'a reason is required to switch the global routing config',
      false,
    );
  }
  return opsApiRequest<SetGlobalRoutingConfigResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/global-default`,
    { method: 'PUT', body: { ...body, reason } },
    fetchImpl,
  );
}

/**
 * On-demand parity between the global default config and the platform default
 * tables (#1918). Read-only; the daily cron arm lives in control-loops and is
 * not reachable from the console.
 */
export async function getGlobalDefaultParity(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GlobalDefaultParityResponse> {
  return opsApiRequest<GlobalDefaultParityResponse>(
    `${BASE}/global-default-parity`,
    { signal },
    fetchImpl,
  );
}

/**
 * Stamp or clear the soft deprecation marker (#1919). It stops NEW assignments
 * and nothing else — orgs already bound keep routing through the config
 * unchanged, so this is safe to call on anything in the library.
 */
export async function setRoutingConfigDeprecation(
  configId: string,
  body: SetRoutingConfigDeprecationRequest,
  fetchImpl: typeof fetch,
): Promise<SetRoutingConfigDeprecationResponse> {
  return opsApiRequest<SetRoutingConfigDeprecationResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/deprecation`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function assignOrgRoutingConfig(
  configId: string,
  orgId: string,
  fetchImpl: typeof fetch,
): Promise<AssignRoutingConfigResponse> {
  return opsApiRequest<AssignRoutingConfigResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/assign-org/${encodeURIComponent(orgId)}`,
    { method: 'PUT' },
    fetchImpl,
  );
}

export async function getOrgRoutingAssignments(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetOrgRoutingAssignmentsResponse> {
  return opsApiRequest<GetOrgRoutingAssignmentsResponse>(
    `${BASE}/orgs/${encodeURIComponent(orgId)}/assignments`,
    { signal },
    fetchImpl,
  );
}

export async function setOrgRoutingAssignments(
  orgId: string,
  body: SetOrgRoutingAssignmentsRequest,
  fetchImpl: typeof fetch,
): Promise<SetOrgRoutingAssignmentsResponse> {
  return opsApiRequest<SetOrgRoutingAssignmentsResponse>(
    `${BASE}/orgs/${encodeURIComponent(orgId)}/assignments`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function renameRoutingConfig(
  configId: string,
  body: RenameRoutingConfigRequest,
  fetchImpl: typeof fetch,
): Promise<RenameRoutingConfigResponse> {
  return opsApiRequest<RenameRoutingConfigResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
}

/**
 * Per-config target pool + policies (epic #886 PR-2 endpoints).
 * `config.assigned_org_count` powers the shared-config impact warning.
 */
export async function cloneRoutingConfig(
  configId: string,
  body: CloneRoutingConfigRequest,
  fetchImpl: typeof fetch,
): Promise<CloneRoutingConfigResponse> {
  return opsApiRequest<CloneRoutingConfigResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/clone`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export interface EffectiveTenantRouteParams {
  readonly org_id: string;
  readonly protocol: UpstreamProtocol;
  readonly sku_id: string;
}

export async function getEffectiveTenantRoute(
  params: EffectiveTenantRouteParams,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetEffectiveTenantRouteResponse> {
  const qs = new URLSearchParams({
    org_id: params.org_id,
    protocol: params.protocol,
    sku_id: params.sku_id,
  });
  return opsApiRequest<GetEffectiveTenantRouteResponse>(
    `${BASE}/effective?${qs.toString()}`,
    { signal },
    fetchImpl,
  );
}

// ─── SKU-keyed pools + policies (epic #898 P5, #909) ────────────────────────

export async function getRoutingConfigSkuSummary(
  configId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetRoutingConfigSkuSummaryResponse> {
  return opsApiRequest<GetRoutingConfigSkuSummaryResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/sku-summary`,
    { signal },
    fetchImpl,
  );
}

export async function listRoutingDefaultSkuTargets(
  skuId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListRoutingSkuTargetsResponse> {
  return opsApiRequest<ListRoutingSkuTargetsResponse>(
    `${BASE}/default-sku-targets/${encodeURIComponent(skuId)}`,
    { signal },
    fetchImpl,
  );
}

export async function setRoutingDefaultSkuTarget(
  skuId: string,
  body: PutRoutingSkuTargetRequest,
  fetchImpl: typeof fetch,
): Promise<PutRoutingSkuTargetResponse> {
  return opsApiRequest<PutRoutingSkuTargetResponse>(
    `${BASE}/default-sku-targets/${encodeURIComponent(skuId)}`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function deleteRoutingDefaultSkuTarget(
  skuId: string,
  targetId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteRoutingSkuTargetResponse> {
  return opsApiRequest<DeleteRoutingSkuTargetResponse>(
    `${BASE}/default-sku-targets/${encodeURIComponent(skuId)}/${encodeURIComponent(targetId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function listRoutingConfigSkuTargets(
  configId: string,
  skuId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListRoutingSkuTargetsResponse> {
  return opsApiRequest<ListRoutingSkuTargetsResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/sku-targets/${encodeURIComponent(skuId)}`,
    { signal },
    fetchImpl,
  );
}

export async function setRoutingConfigCoverageMode(
  configId: string,
  body: SetRoutingConfigCoverageModeRequest,
  fetchImpl: typeof fetch,
): Promise<SetRoutingConfigCoverageModeResponse> {
  return opsApiRequest(`${BASE}/configs/${encodeURIComponent(configId)}/coverage-mode`, {
    method: 'PUT', body,
  }, fetchImpl);
}

export async function previewRoutingConfigSkuCoverageFill(
  body: PreviewRoutingConfigSkuCoverageFillRequest,
  fetchImpl: typeof fetch,
): Promise<PreviewRoutingConfigSkuCoverageFillResponse> {
  return opsApiRequest(`${BASE}/sku-coverage-fill/preview`, { method: 'POST', body }, fetchImpl);
}

export async function applyRoutingConfigSkuCoverageFill(
  body: ApplyRoutingConfigSkuCoverageFillRequest,
  fetchImpl: typeof fetch,
): Promise<ApplyRoutingConfigSkuCoverageFillResponse> {
  return opsApiRequest(`${BASE}/sku-coverage-fill/apply`, { method: 'POST', body }, fetchImpl);
}

export async function setRoutingConfigSkuTarget(
  configId: string,
  skuId: string,
  body: PutRoutingSkuTargetRequest,
  fetchImpl: typeof fetch,
): Promise<RoutingSkuTargetRecord> {
  const response = await opsApiRequest<PutRoutingSkuTargetResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/sku-targets/${encodeURIComponent(skuId)}`,
    { method: 'PUT', body },
    fetchImpl,
  );
  return response.target;
}

export async function deleteRoutingConfigSkuTarget(
  configId: string,
  skuId: string,
  targetId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteRoutingSkuTargetResponse> {
  return opsApiRequest<DeleteRoutingSkuTargetResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/sku-targets/${encodeURIComponent(skuId)}/${encodeURIComponent(targetId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function getRoutingDefaultSkuPolicy(
  skuId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RoutingSkuPolicyRecord | null> {
  const response = await opsApiRequest<GetRoutingSkuPolicyResponse>(
    `${BASE}/default-sku-policies/${encodeURIComponent(skuId)}`,
    { signal },
    fetchImpl,
  );
  return response.policy;
}

export async function setRoutingDefaultSkuPolicy(
  skuId: string,
  body: PutRoutingSkuPolicyRequest,
  fetchImpl: typeof fetch,
): Promise<RoutingSkuPolicyRecord> {
  const response = await opsApiRequest<PutRoutingSkuPolicyResponse>(
    `${BASE}/default-sku-policies/${encodeURIComponent(skuId)}`,
    { method: 'PUT', body },
    fetchImpl,
  );
  return response.policy;
}

export async function deleteRoutingDefaultSkuPolicy(
  skuId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteRoutingSkuPolicyResponse> {
  return opsApiRequest<DeleteRoutingSkuPolicyResponse>(
    `${BASE}/default-sku-policies/${encodeURIComponent(skuId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function getRoutingConfigSkuPolicy(
  configId: string,
  skuId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RoutingSkuPolicyRecord | null> {
  const response = await opsApiRequest<GetRoutingSkuPolicyResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/sku-policies/${encodeURIComponent(skuId)}`,
    { signal },
    fetchImpl,
  );
  return response.policy;
}

export async function setRoutingConfigSkuPolicy(
  configId: string,
  skuId: string,
  body: PutRoutingSkuPolicyRequest,
  fetchImpl: typeof fetch,
): Promise<RoutingSkuPolicyRecord> {
  const response = await opsApiRequest<PutRoutingSkuPolicyResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/sku-policies/${encodeURIComponent(skuId)}`,
    { method: 'PUT', body },
    fetchImpl,
  );
  return response.policy;
}

export async function deleteRoutingConfigSkuPolicy(
  configId: string,
  skuId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteRoutingSkuPolicyResponse> {
  return opsApiRequest<DeleteRoutingSkuPolicyResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/sku-policies/${encodeURIComponent(skuId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}


// ─── Routing time windows (0044) ────────────────────────────────────────────
// Schedules that overlay `weight` / `retry_order` on a target pool. They can
// only re-weight legs the pool already has, so the client has no notion of a
// window "adding" or "removing" a target and there is no verb here that could
// express one.

export async function listRoutingConfigTimeWindows(
  configId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListRoutingTimeWindowsResponse> {
  return opsApiRequest<ListRoutingTimeWindowsResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/time-windows`,
    { signal },
    fetchImpl,
  );
}

/** `windowId === null` creates; otherwise the named window is replaced. */
export async function setRoutingConfigTimeWindow(
  configId: string,
  windowId: string | null,
  body: PutRoutingTimeWindowRequest,
  fetchImpl: typeof fetch,
): Promise<RoutingTimeWindowRecord> {
  const suffix = windowId === null ? '' : `/${encodeURIComponent(windowId)}`;
  const response = await opsApiRequest<PutRoutingTimeWindowResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/time-windows${suffix}`,
    { method: 'PUT', body },
    fetchImpl,
  );
  return response.window;
}

export async function deleteRoutingConfigTimeWindow(
  configId: string,
  windowId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteRoutingTimeWindowResponse> {
  return opsApiRequest<DeleteRoutingTimeWindowResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/time-windows/${encodeURIComponent(windowId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function listRoutingDefaultTimeWindows(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListRoutingTimeWindowsResponse> {
  return opsApiRequest<ListRoutingTimeWindowsResponse>(
    `${BASE}/default-time-windows`,
    { signal },
    fetchImpl,
  );
}

export async function setRoutingDefaultTimeWindow(
  windowId: string | null,
  body: PutRoutingTimeWindowRequest,
  fetchImpl: typeof fetch,
): Promise<RoutingTimeWindowRecord> {
  const suffix = windowId === null ? '' : `/${encodeURIComponent(windowId)}`;
  const response = await opsApiRequest<PutRoutingTimeWindowResponse>(
    `${BASE}/default-time-windows${suffix}`,
    { method: 'PUT', body },
    fetchImpl,
  );
  return response.window;
}

export async function deleteRoutingDefaultTimeWindow(
  windowId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteRoutingTimeWindowResponse> {
  return opsApiRequest<DeleteRoutingTimeWindowResponse>(
    `${BASE}/default-time-windows/${encodeURIComponent(windowId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}
